import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, Save } from "lucide-react";
import { ROLES } from "@/lib/constants";

export default function RolesMatrixPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [edited, setEdited] = useState({}); // { role -> {permissions: Set, responsibility: string} }
  const [saving, setSaving] = useState(null);

  const load = () => {
    api.get("/permissions").then((r) => {
      setData(r.data);
      setEdited({});
    });
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-muted-foreground">Loading roles…</div>;
  const roles = Object.keys(data.roles);
  const canEdit = user?.role === ROLES.SUPER_ADMIN;

  const permsFor = (role) => {
    const overridden = edited[role]?.permissions;
    return overridden || new Set(data.roles[role]);
  };
  const respFor = (role) => {
    if (edited[role]?.responsibility !== undefined) return edited[role].responsibility;
    return data.responsibilities[role] || "";
  };
  const setPerm = (role, key, has) => {
    setEdited((prev) => {
      const cur = new Set(prev[role]?.permissions || data.roles[role]);
      if (has) cur.add(key); else cur.delete(key);
      return { ...prev, [role]: { ...(prev[role] || {}), permissions: cur } };
    });
  };
  const setResp = (role, text) => {
    setEdited((prev) => ({ ...prev, [role]: { ...(prev[role] || {}), responsibility: text } }));
  };

  const save = async (role) => {
    setSaving(role);
    try {
      const payload = {};
      if (edited[role]?.permissions) payload.permissions = Array.from(edited[role].permissions);
      if (edited[role]?.responsibility !== undefined) payload.responsibility = edited[role].responsibility;
      if (Object.keys(payload).length === 0) return;
      await api.put(`/roles/${role}`, payload);
      toast.success(`Permissions updated for ${role.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-roles-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Roles &amp; permissions</h1>
        <p className="text-sm text-muted-foreground">
          {canEdit
            ? "Edit each role's permissions and responsibility description. Changes are written to the audit log."
            : "View each role's permissions and responsibility descriptions. Only Super Admin can edit."}
        </p>
      </div>

      {/* Responsibility cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {roles.map((r) => (
          <div key={r} data-testid={`role-card-${r}`} className="rounded-xl border border-border bg-card/70 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[#e5c158]">{r.replace(/_/g, " ")}</div>
              {canEdit && edited[r]?.responsibility !== undefined && (
                <Button size="sm" variant="outline" data-testid={`role-resp-save-${r}`} onClick={() => save(r)} disabled={saving === r}>
                  <Save className="h-3 w-3 mr-1" /> Save
                </Button>
              )}
            </div>
            {canEdit ? (
              <Textarea
                data-testid={`role-resp-${r}`}
                value={respFor(r)}
                onChange={(e) => setResp(r, e.target.value)}
                rows={3}
                className="text-sm"
              />
            ) : (
              <div className="text-sm text-muted-foreground">{respFor(r)}</div>
            )}
          </div>
        ))}
      </div>

      {/* Permission matrix */}
      <div className="rounded-xl border border-border bg-card/70 overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground sticky left-0 bg-card">Permission</th>
              {roles.map((r) => (
                <th key={r} className="px-3 py-3 text-[10px] uppercase tracking-widest text-muted-foreground text-center">
                  <div>{r.replace(/_/g, " ")}</div>
                  {canEdit && edited[r]?.permissions && (
                    <button data-testid={`role-perms-save-${r}`} onClick={() => save(r)} disabled={saving === r}
                      className="mt-1 text-[10px] normal-case tracking-normal text-[#e5c158] hover:underline">
                      save
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.actions.map((a) => (
              <tr key={a.key} data-row data-testid={`perm-row-${a.key}`} className="border-b border-border/70">
                <td className="px-4 py-3 sticky left-0 bg-card">{a.label}</td>
                {roles.map((r) => {
                  const has = permsFor(r).has(a.key);
                  return (
                    <td key={r} className="text-center py-3">
                      {/* Super Admin always has full access; only other roles are editable. */}
                      {canEdit && r !== ROLES.SUPER_ADMIN ? (
                        <input
                          type="checkbox"
                          data-testid={`perm-${r}-${a.key}`}
                          checked={has}
                          onChange={(e) => setPerm(r, a.key, e.target.checked)}
                          className="h-4 w-4 accent-[#d4af37] cursor-pointer"
                        />
                      ) : (
                        has ? <Check className="inline h-4 w-4 text-[#e5c158]" /> : <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
